import {
  Autocomplete,
  Badge,
  Button,
  Checkbox,
  Collapse,
  InputWrapper,
  Menu,
  Modal,
  NumberInput,
  Popover,
  Select,
  Textarea,
  TextInput,
  Tooltip,
  createTheme,
  type MantineColorsTuple,
  type TransitionOverride,
} from '@mantine/core';

const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

function scaleIn(from: number, origin: string) {
  return {
    in: { opacity: 1, transform: 'scale(1)' },
    out: { opacity: 0, transform: `scale(${from})` },
    common: { transformOrigin: origin },
    transitionProperty: 'opacity, transform',
  };
}

export function dropdownFrom(origin: string): TransitionOverride {
  return {
    transition: scaleIn(0.95, origin),
    duration: 200,
    exitDuration: 150,
    timingFunction: EASE_OUT,
  };
}

const info: MantineColorsTuple = [
  '#e7f3fb',
  '#cfe6f6',
  '#a4cdec',
  '#78b4e2',
  '#4d9bd8',
  '#2a88cc',
  '#1479b8',
  '#10619a',
  '#0c4a75',
  '#083350',
];

// The clinic application's teal; shade 6 is `--color-deep`.
const deep: MantineColorsTuple = [
  '#e8f0f2',
  '#cfdfe3',
  '#a9c5cc',
  '#80a8b2',
  '#5a8b97',
  '#3a6e7c',
  '#1f4049',
  '#162e35',
  '#102329',
  '#09171b',
];

const brand: MantineColorsTuple = [
  '#fff8f5',
  '#fcead6',
  '#fadcbb',
  '#f8cda0',
  '#f6be85',
  '#f4af6d',
  '#f89a44',
  '#b6651c',
  '#935116',
  '#703d10',
];

export const theme = createTheme({
  colors: { brand, deep, info },
  primaryColor: 'brand',
  autoContrast: true,
  black: '#222222',
  fontFamily:
    "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  headings: { fontWeight: '500' },
  radius: { xs: '2px', sm: '6px', md: '8px', lg: '12px', xl: '16px' },
  defaultRadius: 'md',
  activeClassName: 'portal-press',
  respectReducedMotion: true,
  components: {
    Button: Button.extend({
      defaultProps: { radius: 'lg', size: 'md' },
      vars: (mantineTheme, props) => ({
        root:
          (props.color ?? mantineTheme.primaryColor) === 'brand' &&
          (props.variant ?? 'filled') === 'filled'
            ? { '--button-hover': 'var(--color-brand-hover)' }
            : {},
      }),
    }),
    InputWrapper: InputWrapper.extend({
      styles: { description: { color: 'var(--color-muted)' } },
    }),
    // Brand orange is 2.17:1 on white; brand.7 holds 3:1 for a ticked box.
    Checkbox: Checkbox.extend({ defaultProps: { color: 'brand.7' } }),
    Badge: Badge.extend({ defaultProps: { radius: 'sm', size: 'lg' } }),
    TextInput: TextInput.extend({
      defaultProps: { radius: 'sm', size: 'md' },
    }),
    Autocomplete: Autocomplete.extend({
      defaultProps: { radius: 'sm', size: 'md' },
    }),
    NumberInput: NumberInput.extend({
      defaultProps: { radius: 'sm', size: 'md' },
    }),
    Select: Select.extend({
      defaultProps: { radius: 'sm', size: 'md' },
    }),
    Textarea: Textarea.extend({
      defaultProps: { radius: 'sm', size: 'md' },
    }),
    Popover: Popover.extend({
      defaultProps: { transitionProps: dropdownFrom('top') },
    }),
    Menu: Menu.extend({
      defaultProps: { transitionProps: dropdownFrom('top') },
    }),
    Tooltip: Tooltip.extend({
      defaultProps: {
        transitionProps: {
          transition: scaleIn(0.97, 'bottom'),
          duration: 125,
          timingFunction: EASE_OUT,
        },
      },
    }),
    Collapse: Collapse.extend({
      defaultProps: { transitionTimingFunction: EASE_OUT },
    }),
    Modal: Modal.extend({
      defaultProps: {
        radius: 'xl',
        transitionProps: {
          transition: scaleIn(0.96, 'center'),
          duration: 250,
          exitDuration: 150,
          timingFunction: EASE_OUT,
        },
      },
      styles: {
        title: {
          fontSize: 'var(--mantine-font-size-xl)',
          fontWeight: 700,
          textAlign: 'center',
        },
      },
    }),
  },
});
